import classNames from 'classnames'
import { useCallback, useMemo } from 'react'

import type { ChangeEvent, ReactElement } from 'react'
import type { IInAppFiltersCount } from '~/types/iap'
import type { IAppOption } from '~/types/option'

import FilterCounter from './FilterCounter'

import SwitchButton from '~/components/Buttons/SwitchButton'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import RangeInput from '~/components/Inputs/Basic/RangeInput'
import MwMLoader from '~/components/MwMLoader'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import {
  IAP_FREE_TRIAL_FILTERS_MAPPING,
  IAP_FREE_TRIAL_LABEL_MAPPING,
  IAP_PERIODICITY_FILTERS_MAPPING,
  IAP_PERIODICITY_LABEL_MAPPING,
} from '~/const'
import useApps from '~/hooks/useApps'
import { useIapContext } from '~/providers/Iap'
import { useSearchParamsContext } from '~/providers/SearchParams'
import { useFiltersDataListQuery } from '~/queries/iap'
import { IAP_TYPE, IAP_FREE_TRIAL, IAP_PERIODICITY } from '~/types/iap'
import { appKeyDeviceTypeToString } from '~/utils/application'
import { debounce } from '~/utils/function'
import { reactSelectMenuPositionDef } from '~/utils/options'

import './styles.scss'

interface Props {
  className?: string
  modalId?: string
}

const Filters = ({ className, modalId }: Props): ReactElement => {
  /** Local state */

  const componentClasses = classNames('in-apps-filters-component', className)

  const { appsOptions, isAppsLoading } = useApps()

  const formattedAppsOptions = appsOptions.map((el) => ({
    ...el,
    label: `${el.label} - ${el.deviceType}`,
    value: `${el.appKey}/${el.deviceType}`,
  }))

  const { dispatchToSearchParams } = useSearchParamsContext()

  const { dispatch, state } = useIapContext()

  const { apps: stateApps, freeTrial, period, priceRange, type } = state

  const buttons = [
    { content: 'All', id: '', isActive: !type },
    { content: 'Subscriptions', id: IAP_TYPE.SUBSCRIPTION, isActive: type === IAP_TYPE.SUBSCRIPTION },
    { content: 'Products', id: IAP_TYPE.PRODUCT, isActive: type === IAP_TYPE.PRODUCT },
  ]

  /** Queries & Mutations */

  const { data: filterCounts, isLoading } = useFiltersDataListQuery({
    apps: stateApps,
    freeTrial: freeTrial,
    period: period,
    priceRange: priceRange,
    searchQuery: '',
    type,
  })

  /** Handlers */

  const handleAppsFilterChange = (value: IAppOption[]) => {
    const formattedFilter = value.map(({ appKey, deviceType }) => ({
      app_key: appKey,
      device_type: deviceType,
    }))

    dispatch({
      type: 'apps',
      value: formattedFilter,
    })

    dispatchToSearchParams({
      type: 'update',
      value: {
        apps: formattedFilter.map(({ app_key, device_type }) => appKeyDeviceTypeToString(app_key, device_type)),
      },
    })
  }

  const handlePeriodFilterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const castedValue = event.target.value as IAP_PERIODICITY

      const hasFilter = period.find((filter) => filter === event.target.value)

      if (hasFilter) {
        const updatedFilters = period.filter((filter) => filter !== event.target.value)

        dispatch({
          type: 'period',
          value: updatedFilters,
        })
        dispatchToSearchParams({ type: 'update', value: { period: updatedFilters } })
      } else {
        dispatch({
          type: 'period',
          value: [...period, castedValue],
        })
        dispatchToSearchParams({
          type: 'update',
          value: { period: [...period, castedValue] },
        })
      }
    },
    [dispatchToSearchParams, period, dispatch]
  )

  const handleFreeTrialFilterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const castedValue = event.target.value as IAP_FREE_TRIAL

      const hasFilter = freeTrial.find((filter) => filter === event.target.value)

      if (hasFilter) {
        const updatedFilters = freeTrial.filter((filter) => filter !== event.target.value)

        dispatch({ type: 'freeTrial', value: updatedFilters })
        dispatchToSearchParams({ type: 'update', value: { freeTrial: updatedFilters } })
      } else {
        dispatch({
          type: 'freeTrial',
          value: [...freeTrial, castedValue],
        })
        dispatchToSearchParams({
          type: 'update',
          value: { freeTrial: [...freeTrial, castedValue] },
        })
      }
    },
    [dispatchToSearchParams, freeTrial, dispatch]
  )

  const handlePriceRangeFilterChange = useMemo(
    () =>
      debounce((event: { max: number; min: number }) => {
        dispatch({ type: 'priceRange', value: { max: event.max, min: event.min } })
        dispatchToSearchParams({ type: 'update', value: { priceRange: `${event.min}/${event.max}` } })
      }, 500),
    [dispatchToSearchParams, dispatch]
  )

  const handleTypeFilterChange = useCallback(
    (id?: string) => {
      dispatch({ type: 'type', value: id ? (id as IAP_TYPE) : '' })
      dispatchToSearchParams({ type: 'update', value: { type: id ? (id as IAP_TYPE) : '' } })
    },
    [dispatch, dispatchToSearchParams]
  )

  /** Render */

  return (
    <div className={componentClasses} id="add-in-app-modal-filters">
      {isLoading ? (
        <MwMLoader category="item" />
      ) : (
        <>
          <MultiSelectInput
            {...(modalId ? reactSelectMenuPositionDef(modalId) : {})}
            components={{ Option: CustomAppOption }}
            isLoading={isAppsLoading}
            label="Apps"
            onChange={(options) => handleAppsFilterChange(options as IAppOption[])}
            options={formattedAppsOptions}
            value={formattedAppsOptions?.filter(({ appKey, deviceType }) =>
              stateApps.find((app) => app.app_key === appKey && app.device_type === deviceType)
            )}
          />
          <div className="in-apps-filters-component__switch-button-container">
            <div className="in-apps-filters-component__switch-button-container__label">Type</div>
            <SwitchButton
              buttons={buttons}
              className="in-apps-filters-component__switch-button-container__switch-button"
              onButtonSelectionChange={handleTypeFilterChange}
              size="small"
            />
          </div>
          <div className="in-apps-filters-component__checkboxes-container">
            <span className="in-apps-filters-component__checkboxes-container__label">Period</span>
            {Object.values(IAP_PERIODICITY).map((period) => {
              const filterCount = filterCounts
                ? filterCounts[IAP_PERIODICITY_FILTERS_MAPPING[period] as keyof IInAppFiltersCount]
                : null

              return (
                <div className="in-apps-filters-component__checkboxes-container__checkbox-wrapper" key={period}>
                  <CheckboxInput
                    defaultChecked={state.period.includes(period)}
                    disabled={state.type === IAP_TYPE.PRODUCT || !filterCount}
                    id={period}
                    label={IAP_PERIODICITY_LABEL_MAPPING[period]}
                    onChange={handlePeriodFilterChange}
                    value={period}
                  />
                  {filterCount ? <FilterCounter count={filterCount} /> : null}
                </div>
              )
            })}
          </div>
          <div className="in-apps-filters-component__checkboxes-container">
            <span className="in-apps-filters-component__checkboxes-container__label">Free trial</span>
            {Object.values(IAP_FREE_TRIAL).map((freeTrial) => {
              const filterCount = filterCounts
                ? filterCounts[IAP_FREE_TRIAL_FILTERS_MAPPING[freeTrial] as keyof IInAppFiltersCount]
                : null

              return (
                <div className="in-apps-filters-component__checkboxes-container__checkbox-wrapper" key={freeTrial}>
                  <CheckboxInput
                    defaultChecked={state.freeTrial.includes(freeTrial)}
                    disabled={state.type === IAP_TYPE.PRODUCT || !filterCount}
                    id={freeTrial}
                    label={IAP_FREE_TRIAL_LABEL_MAPPING[freeTrial]}
                    onChange={handleFreeTrialFilterChange}
                    value={freeTrial}
                  />
                  {filterCount ? <FilterCounter count={filterCount} /> : null}
                </div>
              )
            })}
          </div>
          <RangeInput
            defaultMaxValue={priceRange.max}
            defaultMinValue={priceRange.min}
            label="Price"
            max={200}
            min={0}
            onChange={handlePriceRangeFilterChange}
          />
        </>
      )}
    </div>
  )
}

export default Filters
